import { TimePeriod } from '../../../enum';
import type { SalesGranularity } from './salesAnalyticsUtils';
export interface SalesViewConfig {
  defaultView: SalesGranularity;
  availableViews: SalesGranularity[];
}

/** ~8 months in days — above this, Weekly is replaced by Bi-Weekly. */
export const BIWEEKLY_RANGE_MONTHS = 8;
export const BIWEEKLY_RANGE_DAYS = BIWEEKLY_RANGE_MONTHS * 30; // 240

export function getCustomRangeDayCount(start: Date, end: Date): number {
  const startMs = new Date(start).setHours(0, 0, 0, 0);
  const endMs = new Date(end).setHours(0, 0, 0, 0);
  return Math.floor((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

/** Whole months in the selected range by duration (~30 days). */
export function getCustomRangeMonthCount(start: Date, end: Date): number {
  const days = getCustomRangeDayCount(start, end);
  return Math.max(Math.round(days / 30), 1);
}

export function shouldUseBiWeeklyViews(rangeDays: number): boolean {
  return rangeDays > BIWEEKLY_RANGE_DAYS;
}

/** Inclusive calendar days that still use Daily-only chart views (~1 month). */
export const DAILY_ONLY_RANGE_DAYS = 31;

export function getSalesViewConfigForCustomRange(
  rangeDays: number
): SalesViewConfig {
  // ≤31 days (e.g. 14 Jun–14 Jul) → Daily only, one point per day
  if (rangeDays <= DAILY_ONLY_RANGE_DAYS) {
    return { defaultView: 'daily', availableViews: ['daily'] };
  }
  // Up to 8 months: Weekly + Monthly
  if (!shouldUseBiWeeklyViews(rangeDays)) {
    return { defaultView: 'weekly', availableViews: ['weekly', 'monthly'] };
  }
  // More than 8 months: Bi-Weekly instead of Weekly
  return { defaultView: 'monthly', availableViews: ['biweekly', 'monthly'] };
}

export function getSalesViewConfig(
  timePeriod: string,
  rangeDays?: number
): SalesViewConfig {
  switch (timePeriod) {
    case TimePeriod.today:
    case TimePeriod.yesterDay:
      return { defaultView: 'daily', availableViews: ['daily'] };
    case TimePeriod.lastSevenDays:
      return { defaultView: 'daily', availableViews: ['daily'] };
    case TimePeriod.lastThirtyDays:
      return { defaultView: 'daily', availableViews: ['daily', 'weekly'] };
    case TimePeriod.lastSixMonths:
      // ~6 months ≤ 8 → keep Weekly
      return { defaultView: 'weekly', availableViews: ['weekly', 'monthly'] };
    case TimePeriod.currentYear: {
      // Jan 1 → today; views depend how far into the year we are
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1);
      const days = getCustomRangeDayCount(yearStart, now);
      return getSalesViewConfigForCustomRange(days);
    }
    case TimePeriod.lastOneYear:
      // 12 months > 8 → Bi-Weekly instead of Weekly
      return { defaultView: 'monthly', availableViews: ['biweekly', 'monthly'] };
    case TimePeriod.customRange:
      return getSalesViewConfigForCustomRange(rangeDays ?? 1);
    default:
      return { defaultView: 'daily', availableViews: ['daily'] };
  }
}

/** Chart bucket counts — can differ from KPI periodDays (e.g. 30 days → 4 weekly buckets). */
export function getChartPointCount(
  timeDuration: string,
  periodDays: number | undefined,
  granularity: SalesGranularity,
  customRangeStart?: Date | null,
  customRangeEnd?: Date | null
): number | undefined {
  if (granularity === 'weekly') {
    if (
      timeDuration === TimePeriod.lastThirtyDays ||
      (timeDuration === TimePeriod.customRange && periodDays && periodDays <= 31)
    ) {
      return 4;
    }
    if (timeDuration === TimePeriod.lastSixMonths) return 26;
    if (
      timeDuration === TimePeriod.customRange &&
      customRangeStart &&
      customRangeEnd
    ) {
      const days = getCustomRangeDayCount(customRangeStart, customRangeEnd);
      return Math.max(Math.ceil(days / 7), 1);
    }
    if (periodDays) return Math.max(Math.ceil(periodDays / 7), 1);
    return undefined;
  }

  if (granularity === 'biweekly') {
    if (timeDuration === TimePeriod.lastOneYear) return 26;
    if (timeDuration === TimePeriod.currentYear) {
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1);
      const days = getCustomRangeDayCount(yearStart, now);
      return Math.max(Math.ceil(days / 14), 1);
    }
    if (
      timeDuration === TimePeriod.customRange &&
      customRangeStart &&
      customRangeEnd
    ) {
      const days = getCustomRangeDayCount(customRangeStart, customRangeEnd);
      return Math.max(Math.ceil(days / 14), 1);
    }
    if (periodDays) return Math.max(Math.ceil(periodDays / 14), 1);
    return undefined;
  }

  if (granularity === 'monthly') {
    if (timeDuration === TimePeriod.lastSixMonths) return 6;
    if (timeDuration === TimePeriod.lastOneYear) return 12;
    if (timeDuration === TimePeriod.currentYear) {
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1);
      return getCustomRangeMonthCount(yearStart, now);
    }
    if (
      timeDuration === TimePeriod.customRange &&
      customRangeStart &&
      customRangeEnd
    ) {
      return getCustomRangeMonthCount(customRangeStart, customRangeEnd);
    }
    if (periodDays) return Math.max(Math.ceil(periodDays / 28), 1);
    return undefined;
  }

  if (timeDuration === TimePeriod.today) {
    return 1;
  }

  return periodDays;
}
